import { FileExcelFilled } from "@ant-design/icons";
import { useApolloClient, useQuery } from "@apollo/client";
import { Button, Col, Empty, Form, Row, Table, Card } from "antd";
import Search from "antd/lib/input/Search";
import { addDays, format, subDays } from "date-fns";
import React, { useEffect, useState } from "react";
import { Link, useMatch, useParams } from "react-router-dom";
import { PdfExportIcon } from "../assets/svg_icons";
import Error from "../components/apollo_error";
import DateRange from "../components/date_range";
import HorizontalScrollContainer from "../components/horizontal_scroll_container";
import LoadingSpinner from "../components/loading_indicator";
import MultiSelect from "../components/multi_select";
import { export_orders, getExportOrderVar } from "../graphql/export";
import {
  getOrdersVar2,
  get_orders2,
  get_total_order_count,
  get_order_item_list,
  getOrderItemListVar,
} from "../graphql/order";
import { ExportExcel } from "../helpers/excel";
import { formatMoney, getCurrency } from "../helpers/utils";
import withClinic from "../hooks/with_clinic";
import paths from "../routes/paths";
import {
  ApolloResult,
  Clinic,
  Order,
  OrderExcel,
  OrderItemExcel,
  OrderItem,
} from "../store";

import useInvoiceTemplate from "../helpers/invoiceHelper";
import { PaginationConfig } from "antd/lib/pagination";
import { Typography } from "antd";
import { Key } from "antd/es/table/interface";

interface P {
  currentClinic?: Clinic;
}

const GtgramSalePage = (props: P) => {
  const clinicId = props.currentClinic?.id || "";

  const client = useApolloClient();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [filter, setFilter] = useState<string[]>([]);
  const [paymentFilter, SetPaymentFilter] = useState<string[]>([]);
  const [pagination, setPagination] = useState<PaginationConfig | null>(null);
  const [exporting, setExporting] = useState(false);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 15),
    endDate: addDays(new Date(), 15),
  });
  const [form] = Form.useForm();
  const take = pagination?.pageSize || 20;
  const skip = ((pagination?.current || 1) - 1) * take;
  const variables = getOrdersVar2({
    clinicId,
    startDate: ranges.startDate,
    endDate: ranges.endDate,
    searchText,
    take,
    skip,
    status: filter,
    payment_status: paymentFilter,
    referralInclude: true,
  });
  const { loading, data, error } = useQuery<ApolloResult<"orders", Order[]>>(
    get_orders2,
    { variables }
  );
  const totalCount = useQuery<
    ApolloResult<"aggregateOrder", { _count: { id: number } }>
  >(get_total_order_count, { variables });

  const { data: orderItemsData, loading: orderItemsLoading } = useQuery<
    ApolloResult<"orderItems", OrderItem[]>
  >(get_order_item_list, {
    variables: getOrderItemListVar({ ids: expandedIds }),
    fetchPolicy: "cache-and-network",
  });

  const { template, loading: invoiceLoading } = useInvoiceTemplate(
    props.currentClinic?.code || ""
  );

  if (loading || invoiceLoading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;
  if (!data || !template) return <Empty />;

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  const onExpandedRowsChange = (expandedKeys: readonly Key[]) => {
    if (expandedKeys.length === 0) return;
    setExpandedIds(expandedKeys.map((key) => key.valueOf().toString()));
  };

  const expandedRowRender = (row: Order) => {
    let columns: any[] = [
      {
        title: "Item Name",
        dataIndex: "name",
        key: "name",
        editable: false,
        render: (_v: string, r: OrderItem) => {
          return (
            r.service?.name ||
            r.service_package?.name ||
            r.product_stock_item?.name ||
            ""
          );
        },
      },
      {
        title: "Price",
        dataIndex: "price",
        key: "price",
        editable: false,
        render: (_v: number, r: OrderItem) => {
          return `${formatMoney(Number(r.price))} ${getCurrency(props.currentClinic)}`;
        },
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        editable: false,
        render: (_v: number, r: OrderItem) => {
          return r.quantity;
        },
      },
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        editable: false,
        render: (_v: number, r: OrderItem) => {
          return `${formatMoney(Number(r.total))} ${getCurrency(props.currentClinic)}`;
        },
      },
    ];
    return (
      <Row>
        <Col span={24}>
          <Card type="inner" title={`Items`} size="small">
            <Table
              loading={orderItemsLoading}
              showHeader={(orderItemsData?.orderItems || []).length > 0}
              size="small"
              columns={columns}
              dataSource={(orderItemsData?.orderItems || []).filter(
                (b) => b.order_id === row.id
              )}
              pagination={{ pageSize: 3 }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const handleExport = async () => {
    const fileName =
      "orders_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    let excelData: any[] = [];
    const header = [
      "Order Date",
      "Order No",
      "Member",
      "Status",
      "Item Name",
      "Item Quantity",
      "Item Price",
      "Net Amount",
      "Total Amount",
      "Payment Made",
      "Balance Due",
      "Discount",
      "Tax",
      "Payment Status",
      "Payment",
      "Phone Number",
      "Seller",
      "Sold By",
    ];

    let list: Order[] = [];
    let rolling = true;
    const take = 500;
    let skip = 0;
    setExporting(true);
    while (rolling) {
      const variables = getExportOrderVar({
        clinicId,
        take,
        skip,
        startDate: ranges.startDate,
        endDate: ranges.endDate,
      });
      const result = await client.query<ApolloResult<"orders", OrderExcel[]>>({
        query: export_orders,
        variables,
      });
      const _orders = result.data?.orders || [];
      list = list.concat(_orders);
      skip += _orders.length;
      rolling = _orders.length === take;
    }
    for (const o of list) {
      let paymentIdx = 0;
      const isSplitPayment = o.payment_method === "SPLIT";
      const orderNo = o.order_id;
      const orderDate = format(new Date(o.created_at), "yyyy-MMM-dd");

      const discount = Number(o.discount);
      const tax = Number(o.tax);
      const totalAmount = Number(o.net_total);

      const payment =
        isSplitPayment && isSplitPayment && o?.payments != null
          ? o.payments[paymentIdx].payment_method
          : o.payment_method;
      const member =
        o.member.clinic_members.length > 0
          ? o.member.clinic_members[0].name
          : o.member.name;
      const phone = o.member.phonenumber;
      const soldBy = o.user.name;
      const paymentMade =
        isSplitPayment && o?.payments != null
          ? o.payments[paymentIdx].payment_amount
          : o.balance;
      const balanceDue = o.credit_balance;
      const paymentSuatus = o.payment_status;
      const seller = o.seller?.display_name;
      const status = o.status;

      for (const [i, item] of o.order_items.entries()) {
        const itemName = getOrderItemName(item);
        const quantity = item.quantity;
        const price = item.price;
        const netAmount = item.total;
        const payments = o?.payments;
        const paymentAmount =
          isSplitPayment && payments != null && paymentIdx < payments.length
            ? payments[paymentIdx].payment_amount
            : "";
        const paymentMethod =
          isSplitPayment && payments != null && paymentIdx < payments.length
            ? payments[paymentIdx].payment_method
            : "";
        excelData.push({
          orderDate,
          orderNo,
          member,
          status,
          itemName,
          quantity,
          price,
          netAmount,
          totalAmount: i === 0 ? totalAmount : "",
          paymentMade: i === 0 ? paymentMade : paymentAmount,
          balanceDue: i === 0 ? balanceDue : "",
          discount: i === 0 ? discount : "",
          tax: i === 0 ? tax : "",
          paymentSuatus: i === 0 ? paymentSuatus : "",
          payment: isSplitPayment
            ? i === 0
              ? payment
              : paymentMethod
            : o.payment_method,
          phone,
          seller,
          soldBy,
        });
        paymentIdx++;
      }

      if (
        isSplitPayment &&
        o?.payments != null &&
        paymentIdx < o.payments.length
      ) {
        for (const [i, payment] of o?.payments.entries()) {
          if (i < paymentIdx) continue;
          const paymentAmount = payment.payment_amount;
          const paymentMethod = payment.payment_method;
          excelData.push({
            orderDate,
            orderNo,
            member,
            status,
            itemName: "",
            quantity: "",
            price: "",
            netAmount: "",
            totalAmount: "",
            paymentMade: paymentAmount,
            balanceDue: "",
            discount: "",
            tax: "",
            paymentSuatus: "",
            payment: paymentMethod,
            phone,
            seller,
            soldBy,
          });
          paymentIdx++;
        }
      }
    }
    ExportExcel(excelData, fileName, header);
    setExporting(false);
  };

  const getOrderItemName = (item: OrderItemExcel) => {
    const metadata = JSON.parse(item.metadata);
    if (metadata?.name) {
      return metadata.name;
    }
    if (item.product_stock_item) {
      return item.product_stock_item.name;
    }
    if (item.member_service_package) {
      return item.member_service_package.service_package.name;
    }
    if (item.service) {
      return item.service.name;
    }
    if (item.service_package) {
      return item.service_package.name;
    }
    return "";
  };

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      editable: false,
      width: 170,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (v: Order, r: Order) => {
        return format(new Date(r.created_at), "dd MMM,yyyy h:mm a");
      },
    },
    {
      title: "OrderNo",
      dataIndex: "order_id",
      key: "order_id",
      editable: false,
      width: 110,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        a.order_id.toLowerCase().localeCompare(b.order_id.toLowerCase()),
      render: (v: number, r: Order) => {
        return r.order_id;
      },
    },
    {
      title: "Member",
      dataIndex: "member",
      key: "member",
      editable: false,
      width: 135,
      sorter: (a: Order, b: Order) =>
        a.member.name.toLowerCase().localeCompare(b.member.name.toLowerCase()),
      render: (v: number, r: Order) => {
        const val =
          r.member.clinic_members.length > 0
            ? r.member.clinic_members[0].name
            : r.member.name;
        return val;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      ellipsis: true,
      sorter: (a: Order, b: Order) => a.status.localeCompare(b.status),
      editable: false,
    },
    {
      title: "Total Amount",
      dataIndex: "total",
      key: "total",
      editable: true,
      width: 134,
      sorter: (a: Order, b: Order) => Number(a.total) - Number(b.total),
      render: (v: number, r: Order) => {
        return `${formatMoney(Number(r.net_total))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Payment Made",
      dataIndex: "balance",
      key: "balance",
      editable: true,
      width: 143,
      sorter: (a: Order, b: Order) => Number(a.balance) - Number(b.balance),
      render: (v: number, r: Order) => {
        return `${formatMoney(Number(r.balance))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Balance Due",
      dataIndex: "credit_balance",
      key: "credit_balance",
      width: 126,
      sorter: (a: Order, b: Order) =>
        Number(a.credit_balance) - Number(b.credit_balance),
      editable: true,
      render: (v: number, r: Order) => {
        return `${formatMoney(Number(r.credit_balance))} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      width: 145,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        a.payment_method.localeCompare(b.payment_status),
      editable: true,
    },
    {
      title: "Payment",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 103,
      ellipsis: true,
      sorter: (a: Order, b: Order) =>
        a.payment_method.localeCompare(b.payment_method),
      editable: true,
    },
    {
      title: "Referral",
      dataIndex: "referral_name",
      key: "referral_name",
      editable: false,
      width: 135,
      sorter: (a: Order, b: Order) =>
        a.referral_name
          ?.toLowerCase()
          .localeCompare(b.referral_name?.toLowerCase() ?? ""),
      render: (v: number, r: Order) => {
        return r.referral_name;
      },
    },
    {
      title: "SoldBy",
      dataIndex: "user",
      key: "user",
      editable: false,
      width: 125,
      sorter: (a: Order, b: Order) =>
        a.user.name.toLowerCase().localeCompare(b.user.name.toLowerCase()),
      render: (v: number, r: Order) => {
        return r.user.name;
      },
    },
  ];
  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>GTGram Sales</Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText || undefined}
              placeholder={`search ${totalCount.data?.aggregateOrder._count.id}...`}
              allowClear
              size="middle"
              onSearch={(val: any) => setSearchText(val)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
      {/* <Col span={2}>
        <Button
          size="middle"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Col> */}
    </Row>
  );
  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              showHeader={data.orders.length > 0}
              dataSource={data.orders.map((b) => ({ key: b.id, ...b }))}
              columns={columns}
              rowClassName="editable-row"
              rowKey={(record: Order) => record.id}
              scroll={{ x: 1650 }}
              onChange={(p: PaginationConfig) => {
                setPagination(p);
              }}
              pagination={{
                total: totalCount.data?.aggregateOrder._count.id || 0,
                current: pagination?.current,
                pageSize: pagination?.pageSize,
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              expandable={{
                expandedRowRender,
                defaultExpandAllRows: false,
                onExpandedRowsChange,
              }}
            />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );
  const filters = <Row style={{ marginTop: 4, marginRight: 2 }}></Row>;

  return (
    <React.Fragment>
      {control}
      {filters}
      {context}
    </React.Fragment>
  );
};

export default withClinic(GtgramSalePage);
